import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, type ProjectDto } from '@pb/shared';
import { archiveProject, createProject, listProjects, updateProject } from '../api/projects';
import { Dialog } from '../components/Dialog';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { useResource } from '../lib/useResource';

function ProjectFormDialog({
  project,
  onClose,
  onSaved,
}: {
  project: ProjectDto | null;
  onClose: () => void;
  onSaved: (project: ProjectDto | null) => void;
}) {
  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const saved = project
        ? await updateProject(project.id, { name: name.trim(), description })
        : await createProject(name.trim(), description);
      onSaved(saved ?? null);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={project ? 'Edit project' : 'New project'}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="project-form" className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : project ? 'Save' : 'Create project'}
          </button>
        </>
      }
    >
      <form
        id="project-form"
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="field">
          <label htmlFor="project-name">Name</label>
          <input
            id="project-name"
            className="input"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="project-description">Description</label>
          <textarea
            id="project-description"
            className="textarea"
            value={description}
            maxLength={4000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </form>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

export function ProjectsPage() {
  const [showArchived, setShowArchived] = useState(false);
  const projects = useResource(() => listProjects(showArchived), [showArchived]);
  const [editing, setEditing] = useState<ProjectDto | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const navigate = useNavigate();

  const toggleArchive = async (project: ProjectDto) => {
    setBusyId(project.id);
    setActionError(null);
    try {
      await archiveProject(project.id, !project.archived);
      await projects.refresh();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusyId(null);
    }
  };

  const list = projects.data ?? [];

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h1>Projects</h1>
          <p className="muted">Each project holds its own template, slide library, groups, presentations and events.</p>
        </div>
        <div className="page-actions">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              aria-label="Show archived"
            />
            Show archived
          </label>
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            New project
          </button>
        </div>
      </div>
      <ErrorAlert error={actionError} />
      {projects.error ? (
        <ErrorState error={projects.error} onRetry={() => void projects.reload()} />
      ) : projects.data === undefined ? (
        <Loading label="Loading projects…" />
      ) : list.length === 0 ? (
        <EmptyState
          title={showArchived ? 'No projects' : 'No active projects'}
          action={
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              Create the first project
            </button>
          }
        >
          {showArchived
            ? 'Create a project to start a slide library.'
            : 'Create a project to start a slide library, or turn on “Show archived” to see archived projects.'}
        </EmptyState>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Presentations</th>
                <th>Slides</th>
                <th>Upcoming events</th>
                <th>Last change</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((project) => (
                <tr key={project.id}>
                  <td>
                    <div className="row">
                      <Link to={`/projects/${project.id}`}>
                        <strong>{project.name}</strong>
                      </Link>
                      {project.archived ? <span className="badge">Archived</span> : null}
                    </div>
                    {project.description ? <div className="small muted">{project.description}</div> : null}
                  </td>
                  <td>{project.presentation_count}</td>
                  <td>{project.slide_count}</td>
                  <td>{project.upcoming_event_count}</td>
                  <td className="nowrap">{formatDate(project.updated_at)}</td>
                  <td className="table-actions">
                    <div className="row" style={{ justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-sm" onClick={() => setEditing(project)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => void toggleArchive(project)}
                        disabled={busyId === project.id}
                      >
                        {project.archived ? 'Unarchive' : 'Archive'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing ? (
        <ProjectFormDialog
          project={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            const wasNew = editing === 'new';
            setEditing(null);
            if (wasNew && saved?.id) navigate(`/projects/${saved.id}`);
            else void projects.refresh();
          }}
        />
      ) : null}
    </div>
  );
}
